import React, { useMemo, useCallback } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { useForm } from 'react-hook-form';
import { ColumnsType } from 'antd/es/table';
import { capitalize } from 'lodash';
import { WarningOutlined } from '@ant-design/icons';

import TableV3, {
  ModifyVariablesArgs,
} from '../../../../components/generic/Table/TableV3';
import { Footer, TitleBar } from '../../../../components';
import QueryErrorBoundary from '../../../../components/error-handling';
import {
  CREATE_IT_APPLICATION,
  DELETE_IT_APPLICATION,
  FETCH_IT_APPLICATIONS,
  UPDATE_IT_APPLICATION,
} from '../../queries/itApplications';
import { cleanUpObject, tableSortingMethods } from '../../../../utils';
import { baseColumns } from './export';
import { templateColumnConfigs } from './template';
import { formatDate } from '../../../../utils/formatDate';
import TitleUpdater from '../../../../components/common/TitleUpdater';
import { RedirectToTierListButton, useTiers } from '../../tiers';
import { TierBadge } from '../../tiers/components';
import { CollapsibleText } from '../../../../components/generic/TextTruncation';
import { APPLICATION_TYPES } from '../../../cyber';

type DataReturned = {
  findAllItApplications: {
    application: string;
    applicationType: string;
    description: string;
    drpDate: string;
    drpLink: string;
    drpInPlace: boolean;
    lastDrpTest: string;
    mto: string;
    rpo: string;
    rto: string;
    sla: string;
    supportsOther: boolean;
    uuid: string;
    tier?: {
      id: number;
      name: string;
      color: string;
    };
  }[];
};

const ITApplications: React.FC = () => {
  const formInstance = useForm();

  const drpLink = formInstance.watch('drpLink');
  const drpInPlace = formInstance.watch('drpInPlace');

  const { data, loading, error } = useQuery<DataReturned>(
    FETCH_IT_APPLICATIONS,
  );

  const { data: tiers } = useTiers();

  const [createITApplication] = useMutation(CREATE_IT_APPLICATION, {
    refetchQueries: ['FindAllItApplications'],
  });

  const [updateITApplication] = useMutation(UPDATE_IT_APPLICATION, {
    refetchQueries: ['FindAllItApplications'],
  });

  const [deleteITApplication] = useMutation(DELETE_IT_APPLICATION, {
    refetchQueries: ['FindAllItApplications'],
  });

  const formattedData = useMemo(() => {
    if (data) {
      return data.findAllItApplications.map((item) => {
        const { tier, ...itemWithoutTier } = item;
        return {
          ...itemWithoutTier,
          tierId: tier?.id?.toString() || null,
          tier: tier, // Preserve the tier object for the column render function
        };
      });
    }
    return [];
  }, [data]);

  const notProvided = useMemo(
    () => <span className="placeholder-text">Not Provided</span>,
    [],
  );

  const columns: ColumnsType<any> = useMemo(
    () => [
      {
        title: 'Application Name',
        dataIndex: 'application',
        key: 'application',
        width: 200,
        align: 'left',
        sorter: (a, b) =>
          tableSortingMethods.string(a.application, b.application),
        render: (text) => <span>{text}</span>,
      },
      {
        title: 'DR Tier',
        dataIndex: 'tier',
        key: 'tier',
        width: 100,
        align: 'center',
        render: (tier) =>
          tier ? (
            <TierBadge color={tier.color} name={tier.name} />
          ) : (
            notProvided
          ),
      },
      {
        title: 'Application Type',
        dataIndex: 'applicationType',
        key: 'applicationType',
        width: 100,
        align: 'center',
        sorter: (a, b) =>
          tableSortingMethods.string(a.applicationType, b.applicationType),
        render: (text) =>
          text ? <span>{capitalize(text)}</span> : notProvided,
      },
      {
        title: 'DRP in Place',
        dataIndex: 'drpInPlace',
        key: 'drpInPlace',
        width: 100,
        align: 'center',
        sorter: (a, b) =>
          tableSortingMethods.string(a.drpInPlace, b.drpInPlace),
        render: (text) => <span>{text ? 'Yes' : 'No'}</span>,
      },
      {
        title: 'DRP Link',
        dataIndex: 'drpLink',
        key: 'drpLink',
        width: 200,
        align: 'left',
        render: (text) =>
          text ? (
            <a href={text} target="_blank" rel="noopener noreferrer">
              {text}
            </a>
          ) : (
            notProvided
          ),
      },
      {
        title: 'DRP Date',
        dataIndex: 'drpDate',
        key: 'drpDate',
        width: 150,
        align: 'center',
        render: (text) =>
          text ? <span>{formatDate(text)}</span> : notProvided,
      },
      {
        title: 'Description',
        dataIndex: 'description',
        key: 'description',
        width: 250,
        align: 'left',
        render: (text: string) => <CollapsibleText text={text} />,
      },
    ],
    [notProvided],
  );

  const fields = useMemo(
    () => [
      {
        key: 'application',
        label: 'Application Name',
        isRequired: true,
      },
      {
        key: 'applicationType',
        label: 'Application Type',
        isRequired: true,
        type: 'select',
        options: [
          { label: 'Enterprise-Wide', value: APPLICATION_TYPES.ENTERPRISE },
          { label: 'Location(s) Specific', value: APPLICATION_TYPES.LOCATIONS },
        ],
        tooltip:
          'Enterprise-Wide refers to an Application that is used by the entire company. Location Specific is an Application only used by specific Locations or Stores.',
        alert: {
          message:
            'Once the IT Application is created, <span class="font-bold">the Application Type can not be modified.</span>',
          type: 'warning',
          icon: <WarningOutlined className="text-yellow-500 text-xl" />,
        },
      },
      {
        key: 'description',
        label: 'Description',
        type: 'textArea',
      },
      {
        key: 'tierId',
        label: 'DR Tier',
        type: 'tierSelect',
        options: tiers?.map((tier) => ({
          label: tier.name,
          value: tier.id.toString(),
          color: tier.color,
        })),
      },
      {
        key: 'mto',
        label: 'Maximum Tolerable Outage (MTO)',
        type: 'time',
        decoratorWord: 'hours',
      },
      {
        key: 'rto',
        label: 'Recovery Time Objective (RTO)',
        type: 'time',
        decoratorWord: 'hours',
      },
      {
        key: 'rpo',
        label: 'Recovery Point Objective (RPO)',
        type: 'time',
        decoratorWord: 'hours',
      },
      {
        key: 'sla',
        label: 'Service Level Agreement (SLA)',
        type: 'time',
        decoratorWord: 'hours',
      },
      {
        key: 'drpInPlace',
        label: 'Application DRP in Place?',
        type: 'boolean',
        alert: {
          message:
            'If No DRP is in place, DRP Link, DRP Date, and Last DRP Test <span class="font-bold">will not be included</span> in the data.',
          type: 'warning',
          icon: <WarningOutlined className="text-yellow-500 text-xl" />,
        },
      },
      {
        key: 'drpLink',
        label: 'DRP Link',
        type: 'link',
        disabled: !drpInPlace,
      },
      {
        key: 'drpDate',
        label: 'DRP Date',
        type: 'date',
        isRequired: !!drpLink,
        disabled: !drpInPlace,
      },
      {
        key: 'lastDrpTest',
        label: 'Last DRP Test',
        type: 'date',
        disabled: !drpInPlace,
      },
      {
        key: 'supportsOther',
        label: 'Supports Other',
        type: 'textArea',
      },
    ],
    [drpLink, drpInPlace, tiers],
  );

  const itApplicationsFormatter = useCallback((data: any) => {
    const mto = data.mto !== '' ? parseInt(data.mto, 10) : 0;
    const rpo = data.rpo !== '' ? parseInt(data.rpo, 10) : 0;
    const rto = data.rto !== '' ? parseInt(data.rto, 10) : 0;
    const sla = data.sla !== '' ? parseInt(data.sla, 10) : 0;

    // tierId is string make it to a number
    const tierId = data.tierId ? parseInt(data.tierId, 10) : null;

    const customData = {
      ...data,
      mto,
      rpo,
      rto,
      sla,
      tierId,
    };

    return cleanUpObject(cleanUpObject(customData));
  }, []);

  const modifyVariables = useCallback(
    ({ values }: ModifyVariablesArgs) => {
      const formattedData = itApplicationsFormatter(values);

      // if isDrpInPlace is false, set drpLink, drpDate, and lastDrpTest to empty strings
      if (!values.drpInPlace) {
        formattedData.drpLink = null;
        formattedData.drpDate = null;
        formattedData.lastDrpTest = null;
      }

      return formattedData;
    },
    [itApplicationsFormatter],
  );

  const templateUploadConfig = {
    duplicateKey: 'application',
    itemLabel: 'Application',
    sampleItemIdentifier: 'Sample Application',
    formatter: itApplicationsFormatter,
  };

  return (
    <>
      <TitleUpdater title="IT Applications" />
      <TitleBar
        title="Data Inputs"
        subtitle="IT Applications"
        brief="Add or remove IT Applications below. This allows MEO to map and quantify exposures to IT Applications."
        rightContent={<RedirectToTierListButton />}
      />
      <QueryErrorBoundary error={error}>
        <TableV3
          title="IT Applications"
          columns={columns}
          data={formattedData}
          loading={loading}
          editable
          formFields={fields}
          createGQL={createITApplication}
          updateGQL={updateITApplication}
          deleteGQL={deleteITApplication}
          modifyVariables={modifyVariables}
          templateColumns={templateColumnConfigs}
          templateUploadConfig={templateUploadConfig}
          exportExcelColumns={baseColumns}
          formInstance={formInstance}
        />
      </QueryErrorBoundary>
      <Footer backPath="/data-inputs" />
    </>
  );
};

export default React.memo(ITApplications);
